import React, {
	forwardRef,
	memo,
	useCallback,
	useImperativeHandle,
	useMemo
} from 'react';
import PropTypes from 'prop-types';

import useForm from '../../../../hooks/useForm';
import Questionaire from '../../Questionaire';

const QuestionaireModule = forwardRef( (
	{
		admin,
		instructions,
		moduleData,
		sections,
		setModuleErrors,
		ui,
		validate
	},
	ref
) => {
	const fieldTypes = useMemo(
		() => ui.reduce(
			(
				acc,
				{
					id,
					type
				}
			) => ( {
				...acc,
				[ id ]: type
			} ),
			{}
		),
		[ ui ]
	);

	const toFormData = data => data.reduce(
		(
			acc,
			{
				questionId,
				questionOptionId,
				value
			}
		) => {
			switch ( fieldTypes[ questionId ] ) {
				case 6: // select
				case 10: // radio
					return {
						...acc,
						[ questionId ]: questionOptionId
					};

				case 4: // group numeric
				case 9: // group checkbox
					return {
						...acc,
						[ questionOptionId
							? `${ questionId }_${ questionOptionId }`
							: `${ questionId }`
						]: value
					};

				default:
					return {
						...acc,
						[ questionId ]: value
					};
			}
		},
		{}
	);

	const {
		formData,
		hasChanged,
		updateField
	} = useForm( toFormData( JSON.parse( moduleData.data ) ) );

	const toData = useCallback( data => JSON.parse( moduleData.data ).map( ( {
		questionId,
		questionOptionId,
		...rest
	} ) => {
		switch ( fieldTypes[ questionId ] ) {
			case 6: // select
			case 10: // radio
				return {
					...rest,
					questionId,
					questionOptionId: data[ `${ questionId }_${ questionOptionId }` ],
					value: ''
				};

			case 4: // group numeric
			case 9: // group checkbox
				return {
					...rest,
					questionId,
					questionOptionId,
					value: data[ `${ questionId }_${ questionOptionId }` ]
				};

			default:
				return {
					...rest,
					questionId,
					questionOptionId,
					value: data[ questionId ]
				};
		}
	} ), [
		fieldTypes,
		moduleData.data
	] );

	const updateModuleData = useCallback( () => {
		const data = toData( formData );

		return hasChanged
			? { ...moduleData, data: JSON.stringify( data ) }
			: null;
	}, [
		formData,
		hasChanged,
		moduleData,
		toData
	] );

	useImperativeHandle( ref, () => ( { updateModuleData } ), [ updateModuleData ] );

	return <Questionaire
		admin={ admin }
		formData={ formData }
		instructions={ instructions }
		sections={ sections }
		setModuleErrors={ setModuleErrors }
		ui={ ui }
		updateField={ updateField }
		validate={ validate }
	/>;
} );

QuestionaireModule.displayName = 'QuestionaireModule';

QuestionaireModule.propTypes = {
	admin: PropTypes.bool,
	instructions: PropTypes.string,
	moduleData: PropTypes.object.isRequired,
	sections: PropTypes.array.isRequired,
	setModuleErrors: PropTypes.func,
	ui: PropTypes.array.isRequired,
	validate: PropTypes.bool
};

export default memo( QuestionaireModule );
